import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../App';
import { resolveMediaUrl } from '../../utils/s3MediaUrl';

/**
 * Image that resolves short-lived presigned S3 GET URLs on mount and retries once on error.
 *
 * @param {Object} props - Passed to <img> except url/context fields
 * @param {string} props.url - Original image_url from API
 * @param {number|string} [props.quoteId]
 * @param {number|string} [props.productImageId]
 * @param {number|string} [props.orderImageId]
 * @param {string} [props.s3Key]
 * @param {string} [props.fallbackUrl]
 */
const S3MediaImage = ({
  url,
  quoteId,
  productImageId,
  orderImageId,
  s3Key,
  fallbackUrl = '',
  alt = '',
  className = '',
  style,
  onClick,
  ...imgProps
}) => {
  const { token } = useContext(AppContext);
  const [src, setSrc] = useState(url || fallbackUrl);
  const [retryCount, setRetryCount] = useState(0);

  const context = { quoteId, productImageId, orderImageId, s3Key };

  useEffect(() => {
    let cancelled = false;

    const load = async (forceRefresh = false) => {
      const base = url || fallbackUrl;
      if (!base) {
        setSrc(fallbackUrl);
        return;
      }
      if (!token) {
        setSrc(base);
        return;
      }
      const resolved = await resolveMediaUrl(
        token,
        forceRefresh ? base : base,
        context
      );
      if (!cancelled) setSrc(resolved || fallbackUrl);
    };

    setRetryCount(0);
    load(false);

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, token, quoteId, productImageId, orderImageId, s3Key, fallbackUrl]);

  const handleError = async () => {
    if (retryCount >= 1 || !token || !url) {
      if (fallbackUrl) setSrc(fallbackUrl);
      return;
    }
    setRetryCount(c => c + 1);
    const refreshed = await resolveMediaUrl(token, url, context);
    setSrc(refreshed || fallbackUrl);
  };

  return (
    <img
      {...imgProps}
      src={src}
      alt={alt}
      className={className}
      style={style}
      onClick={onClick}
      onError={handleError}
    />
  );
};

export default S3MediaImage;
